import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";

import {
  Align,
  BarElement,
  CartesianScaleOptions,
  CartesianTickOptions,
  CategoryScale,
  Chart,
  ChartData,
  ChartEvent,
  ChartOptions,
  ControllerDatasetOptions,
  LayoutPosition,
  Legend,
  LinearScale,
  ScriptableContext,
  ScriptableScaleContext,
  Tooltip,
} from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";

import { gentlyTruncateString } from "utils";

import { CurrencyCode } from "consts";

import { IMPACT_PANEL_RESULT_TYPE } from "models";

Chart.register(BarElement, CategoryScale, ChartDataLabels, Legend, LinearScale, Tooltip);

export type FieldTimelineBarChartProps = {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
  }[];
  formatFunction: (value: number, dividePerHa?: boolean, currency?: CurrencyCode) => string;
  dividePerHa?: boolean;
  currency?: CurrencyCode;
  baseline?: number | null;
  reversedColorScheme?: boolean;
  width?: number;
  height?: number;
  reportingYear: number;
  resultType: IMPACT_PANEL_RESULT_TYPE;
  highlightReportingYearLabel?: boolean;
};

const COLORS = {
  white: "#fff",
  black: "#100C06",
  "green-300": "#B4CEC7",
  "green-400": "#065C45",
  "grey-400": "#d9d9d2",
  "grey-500": "#999",
  "yellow-400": "#ECF86E",
};

const font = {
  family: "dazzed",
  size: 10,
};

const getTextWidth = (text: string, fontStyle: string = `${font.family} ${font.size}px`): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (context) {
    context.font = fontStyle;
    const metrics = context.measureText(text);
    return metrics.width;
  }
  return 0;
};

export const FieldTimelineBarChart = ({
  labels,
  datasets,
  formatFunction,
  dividePerHa,
  currency,
  baseline,
  reversedColorScheme,
  width,
  height,
  reportingYear,
  resultType,
  highlightReportingYearLabel,
}: FieldTimelineBarChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Chart<"bar", number[], string>>(null);
  const [suggestedMin, setSuggestedMin] = useState(0);
  const [suggestedMax, setSuggestedMax] = useState(0);

  const getXAxisMinMax = useCallback(
    (datasets: FieldTimelineBarChartProps["datasets"]) => {
      const stackedValues = datasets?.[0].data.map((_, i) =>
        datasets.reduce((acc, valuesArray) => {
          const sameSignValues = datasets.every((dataset) => dataset.data[i] >= 0) || datasets.every((dataset) => dataset.data[i] < 0);
          // if all the dataset values have the same sign, return the stacked value
          if (sameSignValues) return acc + valuesArray.data[i];
          // if the dataset values have different signs, return the biggest value
          return Math.abs(acc) >= Math.abs(valuesArray.data[i]) ? acc : valuesArray.data[i];
        }, 0)
      );
      // artificially ad 0 as min/max in case of all positive or negative numbers
      const minMax = {
        min: Math.min(0, ...stackedValues),
        max: Math.max(0, ...stackedValues),
      };

      return {
        dataValues: {
          ...minMax,
        },
        labelsLengthInPx: {
          min: getTextWidth(formatFunction(minMax.min, dividePerHa, currency)),
          max: getTextWidth(formatFunction(minMax.max, dividePerHa, currency)),
        },
      };
    },
    [formatFunction, dividePerHa, currency]
  );

  const getSuggestedMinMax = useCallback(
    (chart: Chart<"bar", number[], string>) => {
      if (!chart) return { suggestedMin: 0, suggestedMax: 0 };

      const minMax = getXAxisMinMax(datasets);
      const barChartAreaWidth = chart.scales.x.width;
      const pixelPerPoint = barChartAreaWidth / (Math.abs(chart.scales.x.max) + Math.abs(chart.scales.x.min));
      const minDataLabelPointEquivalent = (minMax.labelsLengthInPx.min + 16) / pixelPerPoint;
      const maxDataLabelPointEquivalent = (minMax.labelsLengthInPx.max + 16) / pixelPerPoint;

      return {
        suggestedMin: minMax.dataValues.min === 0 ? 0 : minMax.dataValues.min - minDataLabelPointEquivalent,
        suggestedMax: minMax.dataValues.max === 0 ? 0 : minMax.dataValues.max + maxDataLabelPointEquivalent,
      };
    },
    [datasets, getXAxisMinMax]
  );

  const options: ChartOptions<"bar"> = {
    plugins: {
      datalabels: {
        formatter: (_value: number, ctx: Context) => {
          const { datasets } = ctx.chart.data as ChartData<"bar", number[], string>;

          const stackedValue = `${formatFunction(
            datasets.reduce((acc, dataset) => acc + dataset.data[ctx.dataIndex], 0),
            dividePerHa,
            currency
          )}`;
          const sameSignValues = datasets.every((dataset) => dataset.data[ctx.dataIndex] >= 0) || datasets.every((dataset) => dataset.data[ctx.dataIndex] <= 0);

          let labelIndexPosition: number;
          if (sameSignValues) {
            // if all data have the same sign, assign the stacked data label to the last available non 0 dataset (default to the last one if they are all 0)
            labelIndexPosition = datasets.reduce((acc, dataset, i) => (dataset.data[ctx.dataIndex] !== 0 ? i : acc), datasets.length - 1);
          } else {
            // if the data have different sign, assign the stacked data label to the biggest number in absolute value
            const label = datasets.reduce(
              (acc, dataset, i) =>
                Math.abs(dataset.data[ctx.dataIndex]) > acc.maxValue ? { indexPosition: i, maxValue: Math.abs(dataset.data[ctx.dataIndex]) } : { ...acc },
              { indexPosition: 0, maxValue: 0 }
            );
            labelIndexPosition = label.indexPosition;
          }

          return ctx.datasetIndex === labelIndexPosition ? stackedValue : null;
        },
        padding: 4,
        align: (ctx: Context) => {
          if ((ctx.dataset.data[ctx.dataIndex] as number) === 0 && ctx.chart.scales.x.max === 0) return "start";
          return (ctx.dataset.data[ctx.dataIndex] as number) < 0 ? "start" : "end";
        },
        anchor: (ctx: Context) => {
          if ((ctx.dataset.data[ctx.dataIndex] as number) === 0 && ctx.chart.scales.x.max === 0) return "start";
          return (ctx.dataset.data[ctx.dataIndex] as number) < 0 ? "start" : "end";
        },
        color: COLORS.black,
        font,
      },
      legend: {
        display: datasets?.length > 1,
        position: "bottom" as LayoutPosition,
        align: "start" as Align,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          useBorderRadius: true,
          borderRadius: 6,
          padding: 8,
          color: COLORS.black,
          font,
        },
        onClick: (e: ChartEvent) => e.native?.stopPropagation(),
      },
      tooltip: {
        enabled: false,
      },
    },
    indexAxis: "y" as ControllerDatasetOptions["indexAxis"],
    scales: {
      x: {
        suggestedMin,
        suggestedMax,
        position: "top" as CartesianScaleOptions["position"],
        border: {
          display: false,
          dash: (ctx: ScriptableScaleContext) => (ctx.tick.value === baseline ? [2, 2] : undefined),
        },
        grid: {
          color: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? "#515151" : "#999999"),
          drawTicks: false,
          z: 1,
        },
        ticks: {
          padding: 0,
          align: "start" as Align,
          font,
          // negative offset the baseline label position by 5px to align the arrow with the tick
          labelOffset: -5,
          // remove tick label for zero x value
          color: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? "transparent" : COLORS.black),
        },
        afterBuildTicks: (scale: LinearScale) => {
          const ticks = [{ value: 0 }];
          if (baseline) ticks.push({ value: baseline });
          scale.ticks = ticks;
        },
        stacked: true,
      },
      y: {
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: COLORS.black,
          backdropColor: COLORS["yellow-400"],
          backdropPadding: 4,
          showLabelBackdrop: (ctx: ScriptableScaleContext) => highlightReportingYearLabel && ctx.tick.label === reportingYear.toString(),
          crossAlign: "far" as CartesianTickOptions["crossAlign"],
          font: (ctx: ScriptableScaleContext) => {
            const font = {
              family: "dazzed",
              size: 10,
              weight: highlightReportingYearLabel && ctx.tick.label === reportingYear.toString() ? 600 : 200,
            };
            return font;
          },
        },
        stacked: true,
      },
    },
    responsive: false,
    maintainAspectRatio: false,
    devicePixelRatio: 4,
    animation: {
      duration: 300,
    },
  };

  const data = {
    labels: labels.map((label) => gentlyTruncateString(label, 15)),
    datasets: datasets.map((dataset) => {
      const unverifiedPositiveValueColor = COLORS["green-300"];
      const verifiedPositiveValueColor = COLORS["green-400"];
      const practicePositiveValueColor = COLORS["green-400"];
      const negativeValueColor = COLORS["grey-500"];

      const paintBar = (ctx: ScriptableContext<"bar">) => {
        const index = ctx.dataIndex;
        const isVerified = Number(labels[index]) < reportingYear;
        const isPositiveValue = (ctx.parsed?.x >= 0 && !reversedColorScheme) || (ctx.parsed?.x < 0 && reversedColorScheme);

        if (isPositiveValue && resultType === IMPACT_PANEL_RESULT_TYPE.ALL_YEARS) {
          return isVerified ? verifiedPositiveValueColor : unverifiedPositiveValueColor;
        }

        if (isPositiveValue && resultType === IMPACT_PANEL_RESULT_TYPE.YEARLY) {
          return practicePositiveValueColor;
        }
        return negativeValueColor;
      };

      return {
        ...dataset,
        backgroundColor: (ctx: ScriptableContext<"bar">) => paintBar(ctx),
        hoverBackgroundColor: (ctx: ScriptableContext<"bar">) => paintBar(ctx),
        borderColor: (ctx: ScriptableContext<"bar">) => paintBar(ctx),
        hoverBorderColor: (ctx: ScriptableContext<"bar">) => paintBar(ctx),
        borderRadius: 50,
        borderWidth: 1,
        barPercentage: 0.4,
        categoryPercentage: 1,
      };
    }),
  };

  useEffect(() => {
    if (chartRef.current && containerRef.current) {
      const { suggestedMin, suggestedMax } = getSuggestedMinMax(chartRef.current);
      setSuggestedMin(suggestedMin);
      setSuggestedMax(suggestedMax);

      const barsCount = datasets?.[0].data.length;
      if (barsCount > 0 && chartRef.current) {
        // 12px circle size + 1px circle border + 8px padding
        const legendHeight = datasets.length > 1 ? 21 : 0;
        // 20px is the category size, the actual bar is 40% -> 8px
        const chartHeight = barsCount * 20 + chartRef.current.chartArea.top + legendHeight;
        // since we had to disable the chart responsiveness, we need to force the canvas to fill the parent div width
        chartRef.current.resize(containerRef.current.offsetWidth, chartHeight);
      }
    }
  }, [datasets, getSuggestedMinMax]);

  return (
    <div ref={containerRef}>
      <Bar ref={chartRef} options={options} data={data} data-testid="field-timeline-bar-chart" width={width} height={height} />
    </div>
  );
};
